import type { Activity, ActivityEvent, ActivityRecord } from '../model/activity.js'
import { estimated, type SummaryValue, unavailable } from './provenance.js'

/**
 * Moving-time estimation and the shared "is the athlete moving here?"
 * classification also used by `calc/cadence.ts`.
 *
 * Moving time is *not* a device measurement here: a device's own
 * `totalMovingTime` comes from its proprietary auto-pause/motion logic,
 * and once records have been cropped/removed/merged the `Activity` alone
 * can't tell whether that session field still describes the retained
 * records. So it is always derived from the retained records with the
 * documented heuristic below and tagged `estimated` (a threshold-based
 * classification, not a measured quantity) — never `original`, never
 * `recalculated`.
 *
 * Semantics (decided for the dashboard summary, see tests in
 * `moving.test.ts`):
 *
 * - Records are taken in timestamp order; records without a `Date`
 *   timestamp are ignored. Each consecutive pair `(a, b)` is one
 *   interval of `dt = b - a` seconds; non-positive `dt` contributes
 *   nothing.
 * - Intervals overlapping an explicit timer-stopped window (timer `stop`/
 *   `stopAll` event -> next timer `start`, or to the end of the
 *   activity if never restarted — the same event semantics
 *   `model/timing.ts` `computeTiming` uses for pauses) never count.
 * - Speed for an interval is the speed recorded at its *end* record `b`
 *   (`enhancedSpeed` preferred over legacy `speed`; a FIT record's speed
 *   describes motion leading up to its timestamp). If `b` carries no
 *   speed, the device cumulative `distance` delta over the interval
 *   divided by `dt` is used instead. No GPS-geometry fallback: an
 *   activity with neither speed nor distance on its records is
 *   `unavailable`.
 * - Moving iff that speed >= `MOVING_SPEED_THRESHOLD_MPS` (0.5 m/s =
 *   1.8 km/h). Inclusive at the boundary. 0.5 m/s sits below a slow
 *   uphill hike / walking pace (~1 m/s and up) so genuine slow movement
 *   is kept, while being above typical stationary GPS-drift speeds a
 *   device reports when standing still — the same order of magnitude as
 *   Garmin/Strava's moving thresholds for foot sports. A single
 *   threshold is used for every sport (cycling stops are well below it
 *   too), so the result never depends on sport metadata.
 * - Gaps: an interval longer than `MAX_MOVING_GAP_SEC` (30 s) only counts
 *   when the *distance* delta across the gap shows an average speed >=
 *   the threshold. A single instantaneous speed sample can't vouch for
 *   a long unrecorded stretch (devices stop writing records while
 *   stationary under smart recording / auto-pause), so a long gap with
 *   only an instantaneous speed — or no data — is not counted.
 */

/** Speed (m/s) at or above which an interval counts as moving. */
export const MOVING_SPEED_THRESHOLD_MPS = 0.5

/** Intervals longer than this (seconds) need distance-delta evidence of movement to count. */
export const MAX_MOVING_GAP_SEC = 30

/** A record's speed in m/s, preferring `enhancedSpeed` over legacy `speed`. */
export function readSpeedMetersPerSecond(r: ActivityRecord): number | undefined {
  const enhanced = r.enhancedSpeed
  if (typeof enhanced === 'number' && Number.isFinite(enhanced)) return enhanced
  if (typeof r.speed === 'number' && Number.isFinite(r.speed)) return r.speed
  return undefined
}

function readDistance(r: ActivityRecord): number | undefined {
  return typeof r.distance === 'number' && Number.isFinite(r.distance) ? r.distance : undefined
}

/** Records with a usable timestamp, in timestamp order (stable; never mutates the input). */
export function timestampedRecordsInOrder(
  records: ActivityRecord[],
): Array<ActivityRecord & { timestamp: Date }> {
  return records
    .filter((r): r is ActivityRecord & { timestamp: Date } => r.timestamp instanceof Date)
    .map((r, index) => ({ r, index }))
    .sort((x, y) => x.r.timestamp.getTime() - y.r.timestamp.getTime() || x.index - y.index)
    .map(({ r }) => r)
}

/**
 * Classifies the interval ending at record `b` (starting at `a`):
 * `true` moving, `false` stopped, `undefined` no speed/distance evidence
 * either way. Does not consider timer pauses (callers do).
 */
export function classifyInterval(
  a: ActivityRecord & { timestamp: Date },
  b: ActivityRecord & { timestamp: Date },
): boolean | undefined {
  const dt = (b.timestamp.getTime() - a.timestamp.getTime()) / 1000
  if (Number.isNaN(dt) || dt <= 0) return undefined
  const da = readDistance(a)
  const db = readDistance(b)
  const distanceSpeed = da != null && db != null ? (db - da) / dt : undefined
  if (dt > MAX_MOVING_GAP_SEC) {
    if (distanceSpeed != null) return distanceSpeed >= MOVING_SPEED_THRESHOLD_MPS
    // Speed alone can't vouch for a long gap: not moving if there is speed
    // data, no evidence at all otherwise.
    return readSpeedMetersPerSecond(b) == null ? undefined : false
  }
  const speed = readSpeedMetersPerSecond(b) ?? distanceSpeed
  if (speed == null) return undefined
  return speed >= MOVING_SPEED_THRESHOLD_MPS
}

interface TimeWindow {
  start: number
  end: number
}

/**
 * Timer-stopped windows from explicit timer events: `stop`/`stopAll` ->
 * next `start`, open-ended if never restarted. Same event semantics as
 * `computeTimingFromRecordsAndEvents` in `model/timing.ts` (which can't
 * express an open-ended trailing pause in its `Pause` shape, hence this
 * small local walk rather than reusing its output).
 */
export function timerStoppedWindows(events: ActivityEvent[]): TimeWindow[] {
  const timerEvents = events
    .filter((e) => e.event === 'timer' && e.timestamp instanceof Date)
    .sort((x, y) => (x.timestamp as Date).getTime() - (y.timestamp as Date).getTime())
  const windows: TimeWindow[] = []
  let stoppedAt: number | undefined
  for (const e of timerEvents) {
    const t = (e.timestamp as Date).getTime()
    if (stoppedAt == null && (e.eventType === 'stop' || e.eventType === 'stopAll')) {
      stoppedAt = t
    } else if (stoppedAt != null && e.eventType === 'start') {
      windows.push({ start: stoppedAt, end: t })
      stoppedAt = undefined
    }
  }
  if (stoppedAt != null) windows.push({ start: stoppedAt, end: Number.POSITIVE_INFINITY })
  return windows
}

/** True when the open interval (t0, t1) overlaps any stopped window (or touches an open/zero-length one). */
export function overlapsStoppedWindow(t0: number, t1: number, windows: TimeWindow[]): boolean {
  return windows.some((w) => t0 < w.end && t1 > w.start)
}

/**
 * Estimated moving time in seconds (see module doc for the exact
 * semantics). `unavailable` when no interval carries any speed/distance
 * evidence (or there are fewer than two timestamped records).
 */
export function computeMovingTimeSec(activity: Activity): SummaryValue<number> {
  const records = timestampedRecordsInOrder(activity.records)
  if (records.length < 2) return unavailable()
  const stopped = timerStoppedWindows(activity.events)

  let movingSec = 0
  let hasEvidence = false
  for (let i = 1; i < records.length; i++) {
    const a = records[i - 1] as ActivityRecord & { timestamp: Date }
    const b = records[i] as ActivityRecord & { timestamp: Date }
    const t0 = a.timestamp.getTime()
    const t1 = b.timestamp.getTime()
    if (Number.isNaN(t1 - t0) || t1 <= t0) continue
    const moving = classifyInterval(a, b)
    if (moving != null) hasEvidence = true
    if (moving !== true) continue
    if (overlapsStoppedWindow(t0, t1, stopped)) continue
    movingSec += (t1 - t0) / 1000
  }

  if (!hasEvidence) return unavailable()
  return estimated(movingSec)
}
